export type ReviewEntityType = 'hotel' | 'experience' | 'tour'

export type ReviewTarget = {
  kind: 'property'
  propertyId: string
  propertyProvider: 'luxuryescapes' | 'bedbank'
} | {
  kind: 'experience'
  experienceId: string
} | {
  kind: 'tour'
  tourId: string
}
export interface ReviewContext {
  target: ReviewTarget
  rating: App.OfferRating
  entityType: ReviewEntityType
}

export interface ReviewSourceCapabilities {
  attribution: 'luxuryescapes' | 'google' | 'external'
  verifiedByLuxuryEscapes: boolean
  supportsDetailedSummary: boolean
  supportsFilters: boolean
  supportsViewAll: boolean
}

const LUXURY_ESCAPES_REVIEW_CAPABILITIES: ReviewSourceCapabilities = {
  attribution: 'luxuryescapes',
  verifiedByLuxuryEscapes: true,
  supportsDetailedSummary: true,
  supportsFilters: true,
  supportsViewAll: true,
}

const GOOGLE_REVIEW_CAPABILITIES: ReviewSourceCapabilities = {
  attribution: 'google',
  verifiedByLuxuryEscapes: false,
  supportsDetailedSummary: false,
  supportsFilters: false,
  supportsViewAll: false,
}

const EXTERNAL_REVIEW_CAPABILITIES: ReviewSourceCapabilities = {
  attribution: 'external',
  verifiedByLuxuryEscapes: false,
  supportsDetailedSummary: true,
  supportsFilters: true,
  supportsViewAll: true,
}

const PAGE_EXTERNAL_REVIEW_CAPABILITIES: ReviewSourceCapabilities = {
  attribution: 'external',
  verifiedByLuxuryEscapes: true,
  supportsDetailedSummary: true,
  supportsFilters: true,
  supportsViewAll: true,
}

export function getReviewSourceCapabilities(
  reviewsSource: string | undefined,
): ReviewSourceCapabilities {
  if (reviewsSource === 'luxuryescapes') return LUXURY_ESCAPES_REVIEW_CAPABILITIES
  if (reviewsSource === 'google') return GOOGLE_REVIEW_CAPABILITIES
  return EXTERNAL_REVIEW_CAPABILITIES
}

/**
 * Standard offer pages have historically exposed the full review experience for
 * every non-Google source. Keep that compatibility contract separate from the
 * source-native capabilities used by embedded review surfaces.
 */
export function getPageReviewSourceCapabilities(
  reviewsSource: string | undefined,
): ReviewSourceCapabilities {
  const capabilities = getReviewSourceCapabilities(reviewsSource)
  if (capabilities.attribution === 'external') return PAGE_EXTERNAL_REVIEW_CAPABILITIES
  return capabilities
}

export function getReviewTargetEntityType(
  target: ReviewTarget | undefined,
): 'hotel' | 'experience' | 'tour' {
  if (target?.kind === 'property') return 'hotel'
  return target?.kind ?? 'experience'
}

export function getOfferPropertyReviewContext(
  property: App.OfferProperty | App.BedbankProperty | undefined,
  propertyProvider: 'luxuryescapes' | 'bedbank',
): ReviewContext | undefined {
  if (!property?.rating?.reviewsTotal || !property.rating.score) return

  return {
    target: {
      kind: 'property',
      propertyId: property.id,
      propertyProvider,
    },
    rating: property.rating,
    entityType: 'hotel',
  }
}

export function getMappedPropertyReviewContext(
  property: App.Property.Property,
  { lpcOfferIds, lpcPropertyIds, bedbankPropertyIds }: App.PropertyOfferMapping,
): ReviewContext | undefined {
  if (!property.rating?.reviewsTotal || !property.rating.score) return

  if (lpcOfferIds.length && lpcPropertyIds.length) {
    return {
      target: {
        kind: 'property',
        propertyId: lpcPropertyIds[0],
        propertyProvider: 'luxuryescapes',
      },
      rating: property.rating,
      entityType: 'hotel',
    }
  }

  if (bedbankPropertyIds.length) {
    return {
      target: {
        kind: 'property',
        propertyId: bedbankPropertyIds[0],
        propertyProvider: 'bedbank',
      },
      rating: property.rating,
      entityType: 'hotel',
    }
  }
}
